<template>
  <div class="select-company page">
    <!-- Logo -->
    <div class="sidebar-logo">
      <img :src="logo_storalytic" alt="Logo Storalytic" />
    </div>

    <div class="user-info">
        <i class="bi bi-person-fill"></i>
        {{ userStore.user.UserName }} {{ userStore.user.UserSurname }} —
        <span v-if="userStore.isSuperUser">{{ $t('auth.selectCompany.superUser') }}</span>
        <span v-if="userStore.isSuperAdmin">{{ $t('auth.selectCompany.superAdmin') }}</span>
        <a class="logout-link" @click="logout">{{ $t('auth.logout') }}</a>
    </div>

    <!-- Super user badges -->
    <div style="padding-top: 40px;">
      <p v-if="userStore.isSuperUser" class="super-msg">
        <b>{{ $t('auth.selectCompany.welcomeSuperUser') }}
          <i class="bi bi-lightning-fill" style="color:darkorange"></i>
        </b>
      </p>
      <p v-if="userStore.isSuperAdmin" class="super-msg">
        <b>"Ge ga hert of ge ga ni"</b>
      </p>
      <p v-if="userStore.isSuperAdmin" class="super-msg">
        <b>{{ $t('auth.selectCompany.welcomeSuperAdmin') }}
          <i class="bi bi-lightning-fill" style="color:red"></i>
        </b>
      </p>
    </div>

    <!-- Loader -->
    <div v-if="isLoading" class="loader-container">
      <div class="loader"></div>
    </div>

    <!-- Content -->
    <div v-else>
      <Section :title="$t('auth.selectCompany.chooseStore')">
        <KpiGrid class="company-grid">
          <div
            v-for="c in options"
            :key="c.CompanyId"
            class="company-card"
            @click="choose(c.CompanyId)"
          >
            <KpiCard
              :label="c.CompanyName"
              icon="bi-building"
              accent="var(--stora-accent)"
              size="md"
              :value="''"
            >
              <!-- Optional meta row: show ID or role, or leave empty -->
              <template #meta>
                <span class="company-meta">{{ $t('auth.selectCompany.companyId', { id: c.CompanyId }) }}</span>
              </template>
            </KpiCard>
          </div>
        </KpiGrid>

        <div class="mt-4" v-if="userStore.isSuperAdmin">
          <button class="stora-btn primary with-icon" @click="goToRegistration">
            <i class="bi bi-building-add"></i>
            <span>{{ $t('auth.selectCompany.registerNew') }}</span>
          </button>
          <!-- Platform-wide holiday periods (bouwverlof …): the count says how many
               calendars have next year's dates due or missing — the same rule as the
               Monday reminder mail. -->
          <button class="stora-btn primary with-icon sector-btn" @click="router.push({ name: 'SectorCalendars' })">
            <i class="bi bi-calendar2-week"></i>
            <span>{{ $t('auth.selectCompany.sectorCalendars') }}</span>
            <span v-if="sectorDue" class="due-count" :title="$t('auth.selectCompany.calendarsDue', sectorDue)">
              {{ $t('auth.selectCompany.due', { n: sectorDue }) }}
            </span>
          </button>
        </div>
      </Section>
    </div>
  </div>
</template>

<script>
import Section from '@/components/ui/Section.vue'
import KpiGrid from '@/components/ui/KpiGrid.vue'
import KpiCard from '@/components/ui/KpiCard.vue'

import axios from 'axios'

import { ref, onBeforeMount, computed } from 'vue'
import { useRouter } from 'vue-router'

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useFlowStore }        from '@/stores/FlowStore'
import { useCameraStreamStore }   from '@/stores/CameraStreamStore'
import { useDetectionZoneStore }  from '@/stores/DetectionZone'
import { useLocationCalendarStore } from '@/stores/LocationCalendarStore'
import { groupCalendars } from '@/calendars/sectorCalendars'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import image from "./icons/logo_storalytic.png"

export default {
  name: 'SelectCompany',

  components: {
    Section,
    KpiGrid,
    KpiCard,
  },

  setup() {
    const userStore = useUserStore()
    const companyStore = useCompanyStore()
    const flowStore = useFlowStore()
    const cameraStreamStore = useCameraStreamStore()
    const detectionZoneStore = useDetectionZoneStore()
    const router = useRouter()

    const toast = useToast()
    const { t } = useI18n()

    const isLoading = ref(true)

    // 1 fetch the master companyList & (for non-Super-Admin) user details
    onBeforeMount(async () => {
      // fetch master companyList so we know names
      await companyStore.fetchCompanyList()
      
      if (!userStore.isSuperAdmin) {
        // only fetch details (and userStore.companies) if NOT a global super-admin
        await userStore.fetchUserDetails()
      }

      isLoading.value = false
    })

    // 2 Build a single “rawTenants” list, depending on admin or not:
    const rawTenants = computed(() => {
      if (userStore.isSuperAdmin) {
        // every company, role ‘Super Admin’
        return companyStore.companyList.map(c => ({
          CompanyId: c.CompanyId,
          role: 'Super Admin'
        }))
      } else {
        // only the tenants this user has access to, with their assigned role
        return userStore.companies
      }
    })

    // 3 Join names in for display
    const options = computed(() =>
      rawTenants.value.map(r => {
        const match = companyStore.companyList.find(x => x.CompanyId === r.CompanyId)
        return {
          CompanyId: r.CompanyId,
          role: r.role,
          CompanyName: match?.CompanyName || `#${r.CompanyId}`
        }
      })
    )
    
    // 4 Clicking a card:
    const choose = async (companyId) => {
      isLoading.value = true

      // 1) swap company in the user store (new token + userStore.currentCompany)
      await userStore.selectCompany(companyId)
      
      // 2) clear & re-fetch every other store that’s scoped by company
      companyStore.clearCompanyState()
      flowStore.clearFlows()
      cameraStreamStore.clearStreams()
      detectionZoneStore.clearDetectionZones()

      // 3) re-fetch under new context
      await Promise.all([
        companyStore.fetchCompanyDetails(companyId),
        flowStore.fetchFlows(),
      ])
      
      // 4) navigate home (so sidebar & pages re-mount under new context)
      router.push('/')
    }

    const logout = async () => {
        // FIRST, while the credentials are still present: expire the httpOnly
        // device_session cookie server-side. JS cannot clear it (known-gaps #3,
        // Stage 1), and after the localStorage wipe below the axios interceptor
        // has no token left to authenticate with.
        await userStore.logoutServerSide()

        localStorage.removeItem('FixedWidgetsData')
        detectionZoneStore.clearDetectionZones()
        cameraStreamStore.clearStreams()
        flowStore.clearFlows()
        userStore.clearUserState()
        companyStore.clearCompanyState()

        localStorage.removeItem('userDetails')
        localStorage.removeItem('authToken')

        // also wipe axios default auth header
        delete axios.defaults.headers.common['Authorization']
        // reset the in‑memory token too
        userStore.token = null

        // Check if the token is removed
        if (!localStorage.getItem('authToken')) {
            console.log("Token successfully removed.")
        } else {
            console.error("Failed to remove the token.")
        }
        
        router.push('/login').then(() => {
            toast.info(t('nav.toast.loggedOut'))
        })
    }

    // Sector calendars needing dates (super-admins only; best effort — never blocks the page).
    const sectorDue = ref(0)
    onBeforeMount(async () => {
      if (!userStore.isSuperAdmin) return
      try {
        const { entries } = await useLocationCalendarStore().fetchSectorCalendarsAdmin()
        sectorDue.value = groupCalendars(entries, new Date().toISOString().slice(0, 10))
          .filter((c) => c.state !== 'ok').length
      } catch (e) {
        sectorDue.value = 0
      }
    })

    const goToRegistration = () => {
      // one-time pass to the registration page
      sessionStorage.setItem('allowRegistrationOnce', '1')
      router.push({ name: 'Registration' })
    }
    
    return {
        router,
        sectorDue,
        logo_storalytic: image,
        choose,
        isLoading,
        options,
        userStore,
        logout,
        goToRegistration,
    }
  }
}
</script>

<style scoped>
.sector-btn { margin-left: 10px; }
.due-count { margin-left: 4px; background: #fff4d6; color: #8a6200; border-radius: 999px; padding: 1px 8px;
             font-size: 0.75rem; font-weight: 700; }
.select-company { padding: 2rem; }

.sidebar-logo img {
  width: 100px; height: auto; margin-bottom: 0; margin-left: -8px;
}

.super-msg { color: #425461; }

.loader-container { display:flex; justify-content:center; padding:4rem 0; }

/* Right-side info in the Section head */
.user-info {
  color:#425461; display:flex; align-items:center; gap:.5rem; flex-wrap:wrap;
}
.logout-link { color:#4F7EB3; cursor:pointer; text-decoration:none; padding-left:5px; }

/* Make each company tile clickable while reusing KpiCard visuals */
.company-grid {
  padding-top: 12px;
  /* auto-fill (not the shared auto-fit): the last row's cards keep the same
     width as the rest instead of stretching to fill empty space.
     300px min so the longest name ("Van Wiemeersch Work-Wear-Repair") fits
     beside the icon without looking cramped. */
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  align-items: start;
}
.company-card { cursor: pointer; }
.company-card :deep(.kpi-card) {
  height: 104px;                 /* every card the same height */
  transition: box-shadow .2s ease, transform .02s ease;
}
.company-card:hover :deep(.kpi-card) { box-shadow: 0 6px 14px rgba(13,38,76,.08); }
/* Keep icon + name on the same row (the shared KpiCard head is flex-wrap:wrap,
   which let the name drop UNDER the icon). The name still clamps to 2 lines. */
.company-card :deep(.kpi-head) { flex-wrap: nowrap; }

/* Company name: max 2 lines with ellipsis, so a long name can't grow the card. */
.company-card :deep(.kpi-label) {
  display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden; line-height: 1.25;
}
/* drop the (empty) value row these cards don't use */
.company-card :deep(.kpi-value) { display: none; }

/* Company ID stays on one line. */
.company-meta {
  color: #6b7a86;
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.stora-btn {
  display:inline-flex; align-items:center; gap:10px;
  padding:12px 16px; border-radius:12px;
  font-weight:600; border:0; cursor:pointer;
  box-shadow:0 4px 10px rgba(13,38,76,.08);
  transition:transform .02s ease, box-shadow .2s ease, background .2s ease;
}
.stora-btn.primary {
  background:#4F7EB3; color:#fff;
}
.stora-btn.primary:hover {
  background:#476fa0; box-shadow:0 8px 18px rgba(13,38,76,.12);
}
.stora-btn.with-icon i { font-size:1.1rem; line-height:1; }
.stora-btn:active { transform:translateY(1px); }

/* Loader (reuse your token look) */
.loader {
  width: 80px; padding: 8px; aspect-ratio: 1; border-radius: 50%;
  background: #B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m); mask: var(--_m);
  -webkit-mask-composite: source-out; mask-composite: subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn) } }
</style>